"use server";

// ============================================================
// CLUBHUB – Kalender speichern (Server Actions)
// ============================================================
// Ohne Sitzung (Demo-Modus, Musterbestand) wird nichts gespeichert:
// die Actions pruefen die Eingabe und melden Erfolg — die Masken
// tragen die Aenderung bis zum Neuladen. Mit Sitzung schreiben sie in
// den Verein der Sitzung, nie in einen aus dem Aufruf:
//   calendar.write       Termine, Absagen, Zusagen fuer andere,
//                        Einladungen, Schichten, Gruppen-/oeffentliche Abos
//   calendar.attendance  Anwesenheit, Gaeste (Probetraining)
//   members.read         das eigene persoenliche Abo
// Die Arbeit macht `lib/kalender/speicher.ts`; fachliche Gruende
// kommen als `{ error, field }` zurueck, nicht als geworfener Fehler.
// ============================================================
import { revalidatePath } from "next/cache";
import { aktuell } from "@/lib/current";
import type { ActionResult } from "@/lib/action-result";
import type { Permission } from "@/lib/permissions";
import type { PrismaClient } from "@/generated/prisma/client";
import { neuerToken } from "@/lib/kalender/ical";
import { kontakteDesZugangs } from "@/lib/kalender/abfragen";
import {
  Abgelehnt,
  aendereTermin,
  erneuereFeed,
  erzeugeFeed,
  kennung,
  ladeEin,
  legeGastAn,
  legeTerminAn,
  loescheSchicht,
  loescheTermin,
  nameTeilen,
  nimmAbsageZurueck,
  pruefeTermin,
  sageAb,
  setzeAnwesenheit,
  setzeZusage,
  speichereSchicht,
  trageAusSchichtAus,
  trageInSchichtEin,
  verschiebeVorkommen,
  type AbsageUmfang,
  type AnwesenheitEintrag,
  type GastErgebnis,
  type Wer,
  type ZusageErgebnis,
} from "@/lib/kalender/speicher";

const OHNE_RECHT: Partial<Record<Permission, string>> = {
  "calendar.write": "Dafür fehlt dir das Recht, Termine zu bearbeiten.",
  "calendar.attendance": "Dafür fehlt dir das Recht, Anwesenheit zu erfassen.",
  "members.read": "Dafür fehlt dir das Recht, den Kalender zu sehen.",
};

function neuLaden() {
  revalidatePath("/kalender", "layout");
  revalidatePath("/gruppen", "layout");
  revalidatePath("/");
}

function alsFehler(e: unknown): ActionResult | null {
  if (e instanceof Abgelehnt) return e.field ? { error: e.message, field: e.field } : { error: e.message };
  return null;
}

/** Im Musterbestand: pruefen, nichts schreiben, Erfolg melden. */
async function imMuster<T extends object>(ergebnis: () => T): Promise<ActionResult<T> | null> {
  if ((await aktuell()).quelle !== "muster") return null;
  try {
    return { success: true, ...ergebnis() };
  } catch (e) {
    const f = alsFehler(e);
    if (f) return f as ActionResult<T>;
    throw e;
  }
}

type Kontext = { db: PrismaClient; wer: Wer; can: (p: Permission) => boolean };

/** Mit Sitzung: Recht pruefen, im Verein der Sitzung schreiben, Seiten auffrischen. */
async function mitRecht<T extends object>(recht: Permission, tun: (k: Kontext) => Promise<T>): Promise<ActionResult<T>> {
  const [{ requirePermission, Forbidden }, { db }] = await Promise.all([import("@/lib/tenant"), import("@/lib/db")]);
  let k: Kontext;
  try {
    const ctx = await requirePermission(recht);
    k = { db, wer: { clubId: ctx.clubId, userId: ctx.userId }, can: ctx.can };
  } catch (e) {
    if (e instanceof Forbidden) return { error: OHNE_RECHT[recht] ?? "Dafür fehlt dir das Recht." };
    throw e;
  }
  try {
    const r = await tun(k);
    neuLaden();
    return { success: true, ...r };
  } catch (e) {
    const f = alsFehler(e);
    if (f) return f as ActionResult<T>;
    throw e;
  }
}

/** Ein Vorkommen aus dem Browser: schwebende Ortszeit oder `null` (Einzeltermin). */
function vorkommen(roh: unknown): string | null {
  return typeof roh === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(roh) ? roh : null;
}

const UMFAENGE = new Set<AbsageUmfang>(["vorkommen", "ab-hier", "termin"]);

// ── Termine ──────────────────────────────────────────────────

export async function terminSpeichern(id: string | null, eingabe: unknown): Promise<ActionResult<{ id: string }>> {
  const tid = id ? kennung(id) : null;
  return (
    (await imMuster(() => {
      pruefeTermin(eingabe);
      return { id: tid ?? "t-01" };
    })) ??
    mitRecht("calendar.write", async ({ db, wer }) => {
      const e = pruefeTermin(eingabe);
      return tid ? aendereTermin(db, wer, tid, e) : legeTerminAn(db, wer, e);
    })
  );
}

export async function terminLoeschen(id: string): Promise<ActionResult<{ serie: boolean }>> {
  return (await imMuster(() => ({ serie: false }))) ?? mitRecht("calendar.write", ({ db, wer }) => loescheTermin(db, wer, kennung(id)));
}

export async function terminAbsagen(id: string, umfang: AbsageUmfang, occurrence: string | null, grund: string | null): Promise<ActionResult<{ anzahl: number }>> {
  if (!UMFAENGE.has(umfang)) return { error: "Was soll abgesagt werden?" };
  if (typeof grund === "string" && grund.length > 500) return { error: "Der Grund ist zu lang (höchstens 500 Zeichen).", field: "grund" };
  return (await imMuster(() => ({ anzahl: 1 }))) ?? mitRecht("calendar.write", ({ db, wer }) => sageAb(db, wer, kennung(id), umfang, vorkommen(occurrence), grund));
}

export async function absageZuruecknehmen(id: string, occurrence: string | null): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht("calendar.write", async ({ db, wer }) => {
      await nimmAbsageZurueck(db, wer, kennung(id), vorkommen(occurrence));
      return {};
    })
  );
}

export async function vorkommenVerschieben(
  id: string,
  occurrence: string,
  daten: { tag: string; von: string; bis: string; grund: string | null },
): Promise<ActionResult> {
  const occ = vorkommen(occurrence);
  if (!occ) return { error: "Dieses Vorkommen gibt es in der Serie nicht." };
  return (
    (await imMuster(() => ({}))) ??
    mitRecht("calendar.write", async ({ db, wer }) => {
      await verschiebeVorkommen(db, wer, kennung(id), occ, daten ?? {});
      return {};
    })
  );
}

// ── Zusagen und Einladungen ──────────────────────────────────

export async function zusageSetzen(id: string, occurrence: string | null, contactId: string, status: string): Promise<ActionResult<ZusageErgebnis>> {
  return (
    (await imMuster(() => ({ nachgerueckt: null, warteplatz: null }))) ??
    mitRecht("calendar.write", ({ db, wer }) => setzeZusage(db, wer, kennung(id), vorkommen(occurrence), kennung(contactId), status))
  );
}

export async function personenEinladen(id: string, contactIds: string[]): Promise<ActionResult<{ anzahl: number }>> {
  return (
    (await imMuster(() => ({ anzahl: Array.isArray(contactIds) ? contactIds.length : 0 }))) ??
    mitRecht("calendar.write", ({ db, wer }) => ladeEin(db, wer, kennung(id), contactIds))
  );
}

// ── Anwesenheit ──────────────────────────────────────────────

export async function anwesenheitSetzen(id: string, occurrence: string | null, eintraege: AnwesenheitEintrag[]): Promise<ActionResult<{ anzahl: number }>> {
  return (
    (await imMuster(() => ({ anzahl: Array.isArray(eintraege) ? eintraege.length : 0 }))) ??
    mitRecht("calendar.attendance", ({ db, wer }) => setzeAnwesenheit(db, wer, kennung(id), vorkommen(occurrence), eintraege))
  );
}

export async function gastErfassen(id: string, occurrence: string | null, name: string): Promise<ActionResult<GastErgebnis>> {
  return (
    (await imMuster(() => {
      const t = typeof name === "string" ? name.trim() : "";
      if (t.length < 2) throw new Abgelehnt("Bitte einen Namen eingeben.", "gast");
      const teile = nameTeilen(t);
      return { id: `gast-${Date.now()}`, ...teile, displayName: teile.firstName ? `${teile.lastName}, ${teile.firstName}` : teile.lastName };
    })) ?? mitRecht("calendar.attendance", ({ db, wer }) => legeGastAn(db, wer, kennung(id), vorkommen(occurrence), name))
  );
}

// ── Helferschichten ──────────────────────────────────────────

export async function schichtSpeichern(
  eventId: string,
  shiftId: string | null,
  daten: { title: string; von: string; bis: string; needed: number; note: string | null },
): Promise<ActionResult<{ id: string; startsAt: string; endsAt: string }>> {
  return (
    (await imMuster(() => ({ id: shiftId ?? `s-neu-${Date.now()}`, startsAt: "", endsAt: "" }))) ??
    mitRecht("calendar.write", ({ db, wer }) => speichereSchicht(db, wer, kennung(eventId), shiftId ? kennung(shiftId) : null, daten ?? {}))
  );
}

export async function schichtLoeschen(shiftId: string): Promise<ActionResult<{ eingetragen: number }>> {
  return (await imMuster(() => ({ eingetragen: 0 }))) ?? mitRecht("calendar.write", ({ db, wer }) => loescheSchicht(db, wer, kennung(shiftId)));
}

export async function helferEintragen(shiftId: string, contactId: string): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht("calendar.write", async ({ db, wer }) => {
      await trageInSchichtEin(db, wer, kennung(shiftId), kennung(contactId));
      return {};
    })
  );
}

export async function helferAustragen(shiftId: string, contactId: string): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht("calendar.write", async ({ db, wer }) => {
      await trageAusSchichtAus(db, wer, kennung(shiftId), kennung(contactId));
      return {};
    })
  );
}

// ── Kalender-Abo ─────────────────────────────────────────────

/**
 * Eine Abo-Adresse erzeugen (oder ihren Schluessel erneuern). Das
 * persoenliche Abo gilt der Person des eigenen Zugangs — welche, sagt
 * die Datenbank, nicht der Aufruf.
 */
export async function aboErzeugen(wunsch: { scope: "PERSONAL" | "GROUP" | "PUBLIC"; groupId?: string }): Promise<ActionResult<{ id: string; token: string }>> {
  const scope = wunsch?.scope;
  if (scope !== "PERSONAL" && scope !== "GROUP" && scope !== "PUBLIC") return { error: "Diese Art von Abo gibt es nicht." };
  return (
    (await imMuster(() => ({ id: `f-neu-${Date.now()}`, token: neuerToken() }))) ??
    mitRecht(scope === "PERSONAL" ? "members.read" : "calendar.write", async ({ db, wer }) => {
      if (scope === "PERSONAL") {
        const [person] = await kontakteDesZugangs(db, wer.clubId, wer.userId!);
        if (!person) throw new Abgelehnt("Dein Zugang ist mit keiner Person im Verein verknüpft — ein persönliches Abo braucht eine.");
        return erzeugeFeed(db, wer, { scope, contactId: person });
      }
      if (scope === "GROUP") return erzeugeFeed(db, wer, { scope, groupId: kennung(wunsch.groupId) });
      return erzeugeFeed(db, wer, { scope });
    })
  );
}

/** Neuer Schluessel fuer eine Abo-Adresse — der alte Link ist sofort ungueltig. */
export async function aboErneuern(feedId: string): Promise<ActionResult<{ token: string }>> {
  return (
    (await imMuster(() => ({ token: neuerToken() }))) ??
    mitRecht("members.read", async ({ db, wer, can }) => {
      const eigene = await kontakteDesZugangs(db, wer.clubId, wer.userId!);
      return erneuereFeed(db, wer, kennung(feedId), (f) => (f.scope === "PERSONAL" ? Boolean(f.contactId && eigene.includes(f.contactId)) : can("calendar.write")));
    })
  );
}
